'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight, Calendar, ExternalLink } from 'lucide-react';
import { PROJECTS, LOGGED_IN_USER } from '@/lib/mockData';
import PriorityBadge from '@/components/ui/PriorityBadge';
import StatusBadge from '@/components/ui/StatusBadge';

export default function DashboardMyTasks() {
  const myTasks = PROJECTS.flatMap((p) =>
    p.tasks
      .filter((t) => t.assigneeId === LOGGED_IN_USER.id && t.status !== 'Done')
      .map((t) => ({ ...t, projectName: p.name }))
  ).slice(0, 8);

  const isOverdue = (date: string) => date < '2026-05-27';
  const isDueToday = (date: string) => date === '2026-05-27';

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h2 className="text-base font-semibold text-foreground">My Tasks</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {myTasks.length} active tasks assigned to you
          </p>
        </div>
        <Link
          href="/my-tasks"
          className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          View all <ArrowRight size={12} />
        </Link>
      </div>

      {myTasks.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-sm text-muted-foreground">No active tasks assigned to you.</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {myTasks.map((task) => (
            <Link
              key={`my-task-${task.id}`}
              href="/task-detail"
              className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/50 transition-colors group"
            >
              {/* Status indicator */}
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  task.status === 'In Progress' ?'bg-blue-500'
                    : task.status === 'In Review' ?'bg-amber-500' :'bg-slate-300'
                }`}
              />

              {/* Task info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                  {task.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {task.projectName}
                </p>
              </div>

              {/* Meta */}
              <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                <StatusBadge status={task.status} />
                <PriorityBadge priority={task.priority} />
                <div
                  className={`flex items-center gap-1 text-xs font-medium ${
                    isOverdue(task.dueDate)
                      ? 'text-red-500'
                      : isDueToday(task.dueDate)
                      ? 'text-amber-600' :'text-muted-foreground'
                  }`}
                >
                  <Calendar size={12} />
                  {isOverdue(task.dueDate)
                    ? 'Overdue'
                    : isDueToday(task.dueDate)
                    ? 'Due today'
                    : task.dueDate}
                </div>
              </div>

              <ExternalLink
                size={14}
                className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
              />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}